import React from "react";
import "./App.css";
import "./i18n";

import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./contexts/AuthContext";
import useLanguageSync from "./hooks/useLanguageSync";
import LanguageRoute from "./components/LanguageRoute";

import Navigation from "./components/Navigation";
import Footer from "./components/Footer";

import HomePage from "./pages/HomePage";
import StorePage from "./pages/StorePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import AffiliatiPage from "./pages/AffiliatiPage";
import CollabPage from "./pages/CollabPage";
import MytharionVttLanding from "./pages/MytharionVttLanding";
import CartomancerLanding from "./pages/CartomancerLanding";
import DndMapMakerLanding from "./pages/landings/DndMapMakerLanding";
import DungeonMasterToolsLanding from "./pages/landings/DungeonMasterToolsLanding";
import DndWorldMapLanding from "./pages/landings/DndWorldMapLanding";
import DndCityGeneratorLanding from "./pages/landings/DndCityGeneratorLanding";
import AdminSocials from "./pages/AdminSocials";

import VTT from "./pages/VTT/page";

import CartomancerProjects from "./pages/cartomancer/ProjectsPage";
import CartomancerEditor from "./pages/cartomancer/EditorPage";

import AdminSetup from "./pages/AdminSetup";
import RedeemPage from "./pages/RedeemPage";

// =========================================================
// LOCALISED PUBLIC PAGES
// Mirror every SEO-relevant public route under /en/* and /it/*
// so Google can index each language with a stable, canonical
// URL.  Unprefixed routes remain the auto-detected default.
// =========================================================
const PUBLIC_ROUTES = [
  { path: "/", element: <HomePage /> },
  { path: "/store", element: <StorePage /> },
  { path: "/product/:productId", element: <ProductDetailPage /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/contact", element: <ContactPage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/terms", element: <TermsPage /> },
  { path: "/cookies", element: <CookiesPage /> },
  { path: "/affiliati", element: <AffiliatiPage /> },
  { path: "/collab", element: <CollabPage /> },
  { path: "/mytharion-vtt", element: <MytharionVttLanding /> },
  { path: "/cartomancer", element: <CartomancerLanding /> },
  { path: "/dnd-map-maker", element: <DndMapMakerLanding /> },
  { path: "/dungeon-master-tools", element: <DungeonMasterToolsLanding /> },
  { path: "/dnd-world-map", element: <DndWorldMapLanding /> },
  { path: "/dnd-city-generator", element: <DndCityGeneratorLanding /> },
];

const buildLocalisedRoutes = (lang) =>
  PUBLIC_ROUTES.map((r) => {
    const localisedPath =
      r.path === "/" ? `/${lang}/*` : `/${lang}${r.path}`;
    // For "/" we can't use `/en/` alone because BrowserRouter needs an exact match
    // to catch just that segment.  Use two entries: exact `/en` and `/en/`.
    return {
      lang,
      path: localisedPath,
      element: r.element,
      isRoot: r.path === "/",
    };
  });

function AppRouter() {
  const location = useLocation();
  useLanguageSync();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (location.pathname.startsWith("/VTT")) {
    return <VTT />;
  }

  const isAppRoute =
    location.pathname === "/app" || location.pathname.startsWith("/app/");

  return (
    <>
      {!isAppRoute && <Navigation />}

      <main className="flex-1">
        <Routes>
          {/* Unprefixed (auto-detected language) */}
          {PUBLIC_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}

          {/* /en/* mirrors */}
          <Route path="/en" element={<LanguageRoute lang="en"><HomePage /></LanguageRoute>} />
          <Route path="/en/" element={<LanguageRoute lang="en"><HomePage /></LanguageRoute>} />
          {PUBLIC_ROUTES.filter((r) => r.path !== "/").map((r) => (
            <Route
              key={`en${r.path}`}
              path={`/en${r.path}`}
              element={<LanguageRoute lang="en">{r.element}</LanguageRoute>}
            />
          ))}

          {/* /it/* mirrors */}
          <Route path="/it" element={<LanguageRoute lang="it"><HomePage /></LanguageRoute>} />
          <Route path="/it/" element={<LanguageRoute lang="it"><HomePage /></LanguageRoute>} />
          {PUBLIC_ROUTES.filter((r) => r.path !== "/").map((r) => (
            <Route
              key={`it${r.path}`}
              path={`/it${r.path}`}
              element={<LanguageRoute lang="it">{r.element}</LanguageRoute>}
            />
          ))}

          {/* AUTH */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* PAYMENT */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />

          {/* USER / ADMIN — no SEO variants */}
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin-setup" element={<AdminSetup />} />
          <Route path="/admin/socials" element={<AdminSocials />} />

          {/* REDEEM */}
          <Route path="/redeem" element={<RedeemPage />} />

          {/* CARTOMANCER APP (behind auth+entitlement) */}
          <Route path="/app" element={<CartomancerProjects />} />
          <Route path="/app/editor/:projectId" element={<CartomancerEditor />} />
        </Routes>
      </main>

      {!isAppRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
